import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import { Brand } from './brandsSlice';
import { apiClient } from '../../services/api';

interface RecommendationsState {
  personalizedRecommendations: Brand[];
  similarBrands: Brand[];
  trendingBrands: Brand[];
  isLoading: boolean;
  error: string | null;
}

const initialState: RecommendationsState = {
  personalizedRecommendations: [],
  similarBrands: [],
  trendingBrands: [],
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchPersonalizedRecommendations = createAsyncThunk(
  'recommendations/fetchPersonalized',
  async (_, { getState, rejectWithValue }) => {
    try {
      // token 由 apiClient 自动附带（如已设置），无需手动注入
      const _state = getState() as RootState;
      if (_state.auth.token) {
        // 确保 apiClient 已持有 token（如登录后刷新页面的场景）
        // apiClient 在构造时会从 localStorage 读取，此处无需重复 setToken
      }
      const response = await apiClient.getPersonalRecommendations();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        (error as Error)?.message || 'Failed to fetch recommendations'
      );
    }
  }
);

export const fetchSimilarBrands = createAsyncThunk(
  'recommendations/fetchSimilar',
  async (brandId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getSimilarBrands(parseInt(brandId, 10));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        (error as Error)?.message || 'Failed to fetch similar brands'
      );
    }
  }
);

const recommendationsSlice = createSlice({
  name: 'recommendations',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearSimilarBrands: state => {
      state.similarBrands = [];
    },
  },
  extraReducers: builder => {
    builder
      // Personalized recommendations
      .addCase(fetchPersonalizedRecommendations.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPersonalizedRecommendations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.personalizedRecommendations =
          action.payload as unknown as Brand[];
      })
      .addCase(fetchPersonalizedRecommendations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Similar brands
      .addCase(fetchSimilarBrands.fulfilled, (state, action) => {
        state.similarBrands = action.payload as unknown as Brand[];
      });
  },
});

export const { clearError, clearSimilarBrands } = recommendationsSlice.actions;
export default recommendationsSlice.reducer;
